import type { Package } from './types';

/**
 * Coming back from paying by card (docs/runbooks/stripe.md). The URL only says where the browser
 * came back from; the plan and the credits land when the payment is confirmed (the provider's
 * webhook, seconds later). So before leaving, the page notes what it is leaving from, and on the
 * way back it compares the reads with that note until what was paid for is there.
 */
export type LeavingForPlan = {
  kind: 'plan';
  from: Package | null;
  to: Package;
  /** The renewal a move down takes effect on (the preview's `effectiveOn`). */
  effectiveOn: string | null;
};
export type LeavingForPack = {
  kind: 'pack';
  credits: number;
  balance: number;
  newestPackAt: string | null;
};
export type Leaving = LeavingForPlan | LeavingForPack;

const storageKey = 'paige.billing.leaving';
/** A note older than this is from another visit, not the one the browser is coming back from. */
const noteLifetimeMs = 60 * 60 * 1000;

/** Notes what the page is leaving from; a browser that stores nothing just gets a plainer return. */
export function noteLeaving(tenantId: string, leaving: Leaving): void {
  try {
    sessionStorage.setItem(storageKey, JSON.stringify({ tenantId, at: Date.now(), leaving }));
  } catch {
    // Storage blocked: the return still works from the reads alone.
  }
}

/** The note for this company and kind, if there is a fresh one. */
export function readLeaving<K extends Leaving['kind']>(
  tenantId: string,
  kind: K,
): Extract<Leaving, { kind: K }> | null {
  try {
    const raw = sessionStorage.getItem(storageKey);
    const note = raw
      ? (JSON.parse(raw) as { tenantId: string; at: number; leaving: Leaving })
      : null;
    if (!note || note.tenantId !== tenantId || note.leaving.kind !== kind) return null;
    if (Date.now() - note.at > noteLifetimeMs) return null;
    return note.leaving as Extract<Leaving, { kind: K }>;
  } catch {
    return null;
  }
}

/** Forgets the note once the return has been read, so a later visit does not repeat it. */
export function forgetLeaving(): void {
  try {
    sessionStorage.removeItem(storageKey);
  } catch {
    // Nothing stored to forget.
  }
}
